import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  parseQuery,
  getAutocompleteSuggestions,
  getCurrentWord,
  removeTokenFromQuery,
} from '../utils/queryParser';
import SyntaxHelpPopover from './SyntaxHelpPopover';
import './SearchQuery.css';

/**
 * Scryfall-style query input for the Pokedex.
 *
 * Props:
 *   value        {string}   — controlled query string
 *   onChange     {fn}       — called with new query string
 *   resultCount  {number}   — shown as "X Pokémon"
 *   allAbilities {string[]} — for ability autocomplete
 */
function SearchQuery({ value, onChange, resultCount, allAbilities = [] }) {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [showHelp, setShowHelp] = useState(false);
  const [suggestions, setSuggestions] = useState(null); // { prefix, completions }
  const [activeSuggestion, setActiveSuggestion] = useState(0);

  const parsed = parseQuery(value);

  // ── Autocomplete ────────────────────────────────────────────────────────────

  const updateSuggestions = useCallback((val, cursorPos) => {
    const word = getCurrentWord(val, cursorPos ?? val.length);
    if (!word) { setSuggestions(null); return; }
    const result = getAutocompleteSuggestions(word, allAbilities);
    setSuggestions(result);
    setActiveSuggestion(0);
  }, [allAbilities]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    updateSuggestions(val, e.target.selectionStart);
  };

  const handleKeyDown = (e) => {
    if (!suggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(i => Math.min(i + 1, suggestions.completions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (suggestions.completions[activeSuggestion]) {
        e.preventDefault();
        applySuggestion(suggestions.completions[activeSuggestion]);
      }
    } else if (e.key === 'Escape') {
      setSuggestions(null);
    }
  };

  const applySuggestion = useCallback((completion) => {
    const input = inputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart;
    const word = getCurrentWord(value, cursorPos);
    const beforeWord = value.slice(0, cursorPos - word.length);
    const afterCursor = value.slice(cursorPos);

    // Rebuild: keep negate prefix if present, replace word content
    const negatePrefix = word.startsWith('-') || word.startsWith('!') ? word[0] : '';
    const newWord = `${negatePrefix}${suggestions.prefix}${completion}`;
    const newValue = (beforeWord + newWord + ' ' + afterCursor).replace(/\s+/g, ' ').trimStart();

    onChange(newValue);
    setSuggestions(null);

    // Move cursor to after the completed word
    requestAnimationFrame(() => {
      const newPos = beforeWord.length + newWord.length + 1;
      input.setSelectionRange(newPos, newPos);
      input.focus();
    });
  }, [value, suggestions, onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setSuggestions(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Token chip removal ──────────────────────────────────────────────────────

  const removeToken = (raw) => {
    onChange(removeTokenFromQuery(value, raw));
  };

  const clearAll = () => onChange('');

  // ── Render ──────────────────────────────────────────────────────────────────

  const hasQuery = value.trim().length > 0;
  const hasErrors = parsed.errors.length > 0;
  const isSorted = parsed.sortBy !== 'id';

  return (
    <div className="sq-wrapper">
      {/* ── Main input row ── */}
      <div className={`sq-input-row ${hasErrors ? 'sq-input-row--error' : ''}`}>
        <span className="sq-icon" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
        </span>

        <input
          ref={inputRef}
          className="sq-input"
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={(e) => updateSuggestions(e.target.value, e.target.selectionStart)}
          placeholder='Search — try "type:fire bst>=500" or just "char"'
          autoComplete="off"
          spellCheck={false}
        />

        {/* Result count */}
        <span className="sq-count">
          {resultCount !== undefined ? `${resultCount.toLocaleString()} Pokémon` : ''}
        </span>

        {/* Sort indicator */}
        {isSorted && (
          <span className="sq-sort-chip">
            ↕ {parsed.sortBy}
            {parsed.sortDir === 'asc' ? ' ↑' : ' ↓'}
          </span>
        )}

        {/* Clear button */}
        {hasQuery && (
          <button className="sq-clear" onClick={clearAll} aria-label="Clear search" title="Clear">
            ×
          </button>
        )}

        {/* Help button */}
        <button
          className="sq-help-btn"
          onClick={() => setShowHelp(v => !v)}
          aria-label="Search syntax help"
          title="Search syntax help"
        >
          ?
        </button>
      </div>

      {/* ── Autocomplete dropdown ── */}
      {suggestions && suggestions.completions.length > 0 && (
        <ul className="sq-dropdown" ref={dropdownRef} role="listbox">
          {suggestions.completions.map((c, i) => (
            <li
              key={c}
              role="option"
              aria-selected={i === activeSuggestion}
              className={`sq-dropdown-item ${i === activeSuggestion ? 'sq-dropdown-item--active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); applySuggestion(c); }}
              onMouseEnter={() => setActiveSuggestion(i)}
            >
              <span className="sq-dropdown-prefix">{suggestions.prefix}</span>
              <span className="sq-dropdown-value">{c}</span>
            </li>
          ))}
        </ul>
      )}

      {/* ── Token chips ── */}
      {parsed.tokens.length > 0 && (
        <div className="sq-chips">
          {parsed.tokens.map((token, i) => (
            <span
              key={`${token.raw}-${i}`}
              className={`sq-chip ${token.negate ? 'sq-chip--negate' : ''}`}
            >
              <span className="sq-chip-field">{token.field}</span>
              <span className="sq-chip-op">{token.op}</span>
              <span className="sq-chip-value">{token.value}</span>
              <button
                className="sq-chip-remove"
                onClick={() => removeToken(token.raw)}
                aria-label={`Remove ${token.raw}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* ── Error messages ── */}
      {hasErrors && (
        <div className="sq-errors">
          {parsed.errors.map((err, i) => (
            <span key={i} className="sq-error">⚠ {err}</span>
          ))}
        </div>
      )}

      {/* ── Syntax help popover ── */}
      {showHelp && (
        <SyntaxHelpPopover
          onClose={() => setShowHelp(false)}
          onInsert={(example) => {
            const sep = value && !value.endsWith(' ') ? ' ' : '';
            onChange(value + sep + example);
            setShowHelp(false);
            inputRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}

export default SearchQuery;
